import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, User, ShoppingCart } from 'lucide-react';
import api from '../lib/api';
import { useSignupModal } from '../context/SignupModalContext';

import { BrandLogo } from '../lib/branding';

const PublicHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openSignupModal } = useSignupModal();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuPages, setMenuPages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    loadMenu();
  }, []);

  const loadMenu = async () => {
    try {
      const response = await api.get('/menu');
      setMenuPages(response.data);
    } catch (error) {
      console.error('Error loading menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const isActive = (slug) => {
    return location.pathname === slug;
  };

  return (
    <header className="bg-white border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <BrandLogo size="md" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {!loading && menuPages.map((page) => (
              <Link
                key={page.id}
                to={page.slug}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(page.slug)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
                data-testid={`menu-link-${page.slug}`}
              >
                {page.title}
              </Link>
            ))}
          </nav>

          {/* Right Actions - Desktop */}
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                  data-testid="dashboard-button"
                >
                  <User className="h-4 w-4 mr-2" />
                  {user.pseudo}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  Déconnexion
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                  data-testid="login-button"
                >
                  Connexion
                </Button>
                <Button
                  size="sm"
                  onClick={openSignupModal}
                  data-testid="signup-button"
                >
                  S'inscrire
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-md hover:bg-accent"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t" data-testid="mobile-menu">
          <nav className="px-4 py-2 space-y-1">
            {!loading && menuPages.map((page) => (
              <Link
                key={page.id}
                to={page.slug}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive(page.slug)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-menu-link-${page.slug}`}
              >
                {page.title}
              </Link>
            ))}
          </nav>

          <div className="px-4 py-3 border-t space-y-2">
            {user ? (
              <>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    navigate('/dashboard');
                    setMobileMenuOpen(false);
                  }}
                >
                  <User className="h-4 w-4 mr-2" />
                  {user.pseudo}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                >
                  Déconnexion
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    navigate('/login');
                    setMobileMenuOpen(false);
                  }}
                >
                  Connexion
                </Button>
                <Button
                  className="w-full"
                  onClick={() => {
                    openSignupModal();
                    setMobileMenuOpen(false);
                  }}
                >
                  S'inscrire
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
